import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/firebase-config';
import { Plus, Edit, Trash2, X, Printer, Scissors, Package, Warehouse, Hand, Zap, Slice } from 'lucide-react';

// --- Iconos Disponibles ---
const iconMap = {
    Printer: <Printer />,
    Scissors: <Scissors />,
    Blade: <Slice />,
    Laser: <Zap />,
    Package: <Package />,
    Hands: <Hand />,
    Warehouse: <Warehouse />,
};

const MachineModal = ({ isOpen, onClose, onSave, machine }) => {
    const [name, setName] = useState('');
    const [category, setCategory] = useState('Impresión');
    const [icon, setIcon] = useState('Printer');
    const [workAreaWidth, setWorkAreaWidth] = useState('');
    const [workAreaHeight, setWorkAreaHeight] = useState('');
    // --- NUEVO ESTADO PARA TIPO Y UNIDADES ---
    const [formatType, setFormatType] = useState('Plancha');
    const [workAreaUnits, setWorkAreaUnits] = useState('mm');


    useEffect(() => {
        if (machine) {
            setName(machine.name || '');
            setCategory(machine.category || 'Impresión');
            setIcon(machine.icon || 'Printer');
            setWorkAreaWidth(machine.workAreaWidth || '');
            setWorkAreaHeight(machine.workAreaHeight || '');
            setFormatType(machine.formatType || 'Plancha');
            setWorkAreaUnits(machine.workAreaUnits || 'mm');
        } else {
            // Reset to defaults for new machine
            setName('');
            setCategory('Impresión');
            setIcon('Printer');
            setWorkAreaWidth('');
            setWorkAreaHeight('');
            setFormatType('Plancha');
            setWorkAreaUnits('mm');
        }
    }, [machine, isOpen]);

    const handleSave = () => {
        if (!name) {
            alert("El nombre de la máquina es obligatorio.");
            return;
        }
        onSave({ 
            name, 
            category, 
            icon, 
            formatType,
            workAreaUnits,
            workAreaWidth: Number(workAreaWidth) || 0, 
            workAreaHeight: formatType === 'Plancha' ? Number(workAreaHeight) || 0 : 0,
        });
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4">
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-md">
                <div className="flex justify-between items-center p-4 border-b">
                    <h3 className="text-lg font-bold">{machine ? 'Editar Máquina' : 'Nueva Máquina'}</h3>
                    <button onClick={onClose}><X size={24} /></button>
                </div>
                <div className="p-6 space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Nombre de la Máquina / Estación</label>
                        <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full p-2 border rounded-md mt-1" placeholder="Ej: UV Hybrid (Polaroid)" />
                    </div>
                    
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Formato de Trabajo</label>
                        <div className="grid grid-cols-2 gap-4 mt-1">
                             <select value={formatType} onChange={(e) => setFormatType(e.target.value)} className="w-full p-2 border rounded-md">
                                <option>Plancha</option>
                                <option>Rollo</option>
                            </select>
                             <select value={workAreaUnits} onChange={(e) => setWorkAreaUnits(e.target.value)} className="w-full p-2 border rounded-md">
                                <option>mm</option>
                                <option>in</option>
                                <option>ft</option>
                                <option>m</option>
                            </select>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Ancho ({workAreaUnits})</label>
                            <input type="number" value={workAreaWidth} onChange={(e) => setWorkAreaWidth(e.target.value)} className="w-full p-2 border rounded-md mt-1" placeholder="Ej: 1220" />
                        </div>
                        {formatType === 'Plancha' && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Alto ({workAreaUnits})</label>
                                <input type="number" value={workAreaHeight} onChange={(e) => setWorkAreaHeight(e.target.value)} className="w-full p-2 border rounded-md mt-1" placeholder="Ej: 2440" />
                            </div>
                        )}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Categoría</label>
                        <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full p-2 border rounded-md mt-1">
                            <option>Impresión</option>
                            <option>Corte</option>
                            <option>Acabado</option>
                            <option>Almacén</option>
                            <option>Proceso Manual</option>
                            <option>Proceso Externo</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Icono</label>
                        <select value={icon} onChange={(e) => setIcon(e.target.value)} className="w-full p-2 border rounded-md mt-1">
                            {Object.keys(iconMap).map(iconName => (
                                <option key={iconName} value={iconName}>{iconName}</option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className="flex justify-end p-4 bg-gray-50 border-t">
                    <button onClick={onClose} className="px-4 py-2 bg-gray-200 rounded-md mr-2">Cancelar</button>
                    <button onClick={handleSave} className="px-4 py-2 bg-blue-600 text-white font-bold rounded-md">Guardar</button>
                </div>
            </div>
        </div>
    );
};

const MachinesView = () => {
    const [machines, setMachines] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedMachine, setSelectedMachine] = useState(null);

    useEffect(() => {
        const machinesCollection = collection(db, 'machines');
        const unsubscribe = onSnapshot(machinesCollection, (snapshot) => {
            const machinesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setMachines(machinesData);
            setIsLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const handleSaveMachine = async (machineData) => {
        if (selectedMachine) {
            // Update
            const machineRef = doc(db, 'machines', selectedMachine.id);
            await updateDoc(machineRef, machineData);
        } else {
            // Create
            await addDoc(collection(db, 'machines'), {
                ...machineData,
                createdAt: serverTimestamp()
            });
        }
        setIsModalOpen(false);
        setSelectedMachine(null);
    };

    const handleDeleteMachine = async (machineId) => {
        if (window.confirm("¿Está seguro de que desea eliminar esta máquina?")) {
            await deleteDoc(doc(db, 'machines', machineId));
        }
    };

    const openModal = (machine = null) => {
        setSelectedMachine(machine);
        setIsModalOpen(true);
    };

    if (isLoading) return <p className="p-4">Cargando máquinas...</p>;

    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-800">Configuración de Máquinas y Estaciones</h1>
                <button onClick={() => openModal()} className="px-4 py-2 bg-blue-600 text-white font-bold rounded-md flex items-center gap-2">
                    <Plus size={18} /> Nueva Máquina
                </button>
            </div>

            <div className="bg-white shadow-md rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Categoría</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Formato (Work Area)</th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Icono</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {machines.map(machine => (
                            <tr key={machine.id}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{machine.name}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{machine.category}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {machine.workAreaWidth ? (
                                        machine.formatType === 'Rollo' ?
                                        `${machine.workAreaWidth} ${machine.workAreaUnits || ''} (Rollo)` :
                                        `${machine.workAreaWidth} x ${machine.workAreaHeight || 0} ${machine.workAreaUnits || ''} (Plancha)`
                                    ) : 'N/A'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 flex justify-center">{iconMap[machine.icon]}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <button onClick={() => openModal(machine)} className="text-indigo-600 hover:text-indigo-900 mr-4"><Edit size={18}/></button>
                                    <button onClick={() => handleDeleteMachine(machine.id)} className="text-red-600 hover:text-red-900"><Trash2 size={18}/></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <MachineModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSave={handleSaveMachine} machine={selectedMachine} />
        </div>
    );
};

export default MachinesView;

